import { Tooltip } from '@chiroup/components/Tooltip';
import { Check, Clipboard, Info } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../../lib/utils';

interface MetricInfoTooltipProps {
  info?: string;
  /** Raw Salesforce SOQL behind the metric, shown as a copyable block so the team can run it directly. */
  query?: string;
  className?: string;
}

const QueryBlock = ({ query }: { query: string }) => {
  const [copied, setCopied] = useState(false);

  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(query).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => setCopied(false)
    );
  };

  return (
    <div className="mt-2 border-t border-white/15 pt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
          Salesforce query
        </span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-white/10 hover:bg-white/20 transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3" /> Copied
            </>
          ) : (
            <>
              <Clipboard className="h-3 w-3" /> Copy
            </>
          )}
        </button>
      </div>
      <pre className="max-h-56 overflow-auto whitespace-pre rounded bg-black/30 p-2 text-[11px] leading-snug font-mono select-text">
        {query}
      </pre>
      <p className="mt-1 text-[10px] opacity-70">
        Replace {'{startDate}'} / {'{endDate}'} with your range (YYYY-MM-DD) before running.
      </p>
    </div>
  );
};

/**
 * Info icon that reveals a metric's criteria and calculation logic on hover,
 * plus the raw Salesforce query (when the metric is backed by one) so the team
 * can run the report directly. Renders nothing when no info or query is provided.
 */
const MetricInfoTooltip = ({ info, query, className }: MetricInfoTooltipProps) => {
  if (!info && !query) return null;

  const content = (
    <div className="text-left">
      {info && <p className="leading-relaxed">{info}</p>}
      {query && <QueryBlock query={query} />}
    </div>
  );

  return (
    <Tooltip
      content={content}
      side="top"
      delay={150}
      className={query ? 'max-w-[min(90vw,34rem)]' : 'max-w-sm text-left leading-relaxed'}
      disableHoverableContent={!query}
    >
      <button
        type="button"
        aria-label="How this metric is calculated"
        onClick={e => e.stopPropagation()}
        className={cn(
          'inline-flex flex-shrink-0 items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors cursor-help',
          className
        )}
      >
        <Info className="h-4 w-4" />
      </button>
    </Tooltip>
  );
};

export default MetricInfoTooltip;
